import { Link, useLocation } from "react-router-dom";
import { LogOut, Settings, HelpCircle } from "lucide-react";
import { Button } from "@/components/atoms";
import { AppLogo } from "@/components/atoms/AppLogo";
import { StartupSelector } from "@/components/molecules/StartupSelector";

type AppHeaderProps = {
  displayName?: string;
  onSettingsClick: () => void;
  onSignOut: () => void;
};

export function AppHeader({
  displayName,
  onSettingsClick,
  onSignOut,
}: AppHeaderProps) {
  const location = useLocation();
  const isSettingsActive = location.pathname.startsWith("/settings");
  const isHelpActive = location.pathname.startsWith("/help");

  return (
    <header className="sticky top-0 z-40 border-b border-white/[0.08] bg-page/95 backdrop-blur-md">
      <div className="flex w-full items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex min-w-0 shrink-0 items-center gap-4">
          <AppLogo to="/dashboard" size={34} />
          <div className="hidden h-5 w-px bg-surface/10 sm:block" />
          <StartupSelector className="hidden text-white sm:flex" />
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <div className="sm:hidden">
            <StartupSelector className="text-white text-xs" />
          </div>
          <span className="hidden max-w-[140px] truncate text-sm text-white/55 md:inline">
            {displayName || "Account"}
          </span>
          <Link
            to="/help"
            className={`inline-flex items-center rounded-[10px] px-3 py-2 text-sm font-semibold transition-colors ${
              isHelpActive
                ? "bg-brand/15 text-brand ring-1 ring-brand/30"
                : "text-white/55 hover:bg-surface/10 hover:text-white"
            }`}
          >
            <HelpCircle className="mr-1.5 h-4 w-4" />
            Help
          </Link>
          <Button
            variant={isSettingsActive ? "primary" : "secondary"}
            size="sm"
            onClick={onSettingsClick}
          >
            <Settings className="mr-1.5 h-4 w-4" />
            Settings
          </Button>
          <button
            type="button"
            onClick={onSignOut}
            className="rounded-[10px] p-2 text-white/55 transition-colors hover:bg-surface/10 hover:text-white"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
